import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { GOLD_VEC } from "../lib/storm";
import { surgeVert, surgeFrag } from "../lib/shaders";

// Animated wind-swept arrows flowing between the cloud deck and the atmosphere.
export default function WindField({ sim, rMin = 0.9, rMax = 0.98, count = 220, diagonal = false }) {
  const meshRef = useRef();
  const matRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const arrows = useMemo(() => {
    const bands = [
      { latMin: 0, latMax: 30, dir: -1 },   // NE trade winds (easterly)
      { latMin: 30, latMax: 60, dir: 1 },    // westerlies
      { latMin: 60, latMax: 85, dir: -1 },   // polar easterlies
      { latMin: -30, latMax: 0, dir: -1 },   // SE trade winds
      { latMin: -60, latMax: -30, dir: 1 },  // southern westerlies
      { latMin: -85, latMax: -60, dir: -1 },
    ];
    const out = [];
    for (let i = 0; i < count; i++) {
      const b = bands[i % bands.length];
      const lat = b.latMin + Math.random() * (b.latMax - b.latMin);
      out.push({
        lat,
        lon0: Math.random() * 360 - 180,
        radius: rMin + Math.random() * (rMax - rMin),
        dir: b.dir,
        speed: 6 + Math.random() * 8,
        phase: Math.random() * 1000,
        scale: 0.02 + Math.random() * 0.02,
      });
    }
    return out;
  }, [count, rMin, rMax]);

  const geo = useMemo(() => {
    // thin elongated streak that reads as a glowing flow-line
    const g = new THREE.BoxGeometry(0.02, 0.02, 1.0);
    g.translate(0, 0, -0.5);        // pivot at head so it trails behind travel
    return g;
  }, []);

  const CYAN = useMemo(() => new THREE.Color("#3fd0ff"), []);

  const toVec = (lat, lon, r, target) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    target.set(
      -r * Math.sin(phi) * Math.cos(theta),
      r * Math.cos(phi),
      r * Math.sin(phi) * Math.sin(theta)
    );
  };

  const p = useMemo(() => new THREE.Vector3(), []);
  const p2 = useMemo(() => new THREE.Vector3(), []);

  useFrame((state) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = state.clock.elapsedTime;
    const intensity = sim.current.intensity / 5;
    const turbo = 1 + intensity * 2.2;
    for (let i = 0; i < arrows.length; i++) {
      const a = arrows[i];
      const lon = a.lon0 + a.dir * (a.phase + t * a.speed * turbo);
      const wobble = Math.sin(t * 3 + a.phase) * intensity * 4;
      const latTilt = diagonal ? 1.6 : 0;
      toVec(a.lat + wobble, lon, a.radius, p);
      toVec(a.lat + wobble + latTilt, lon + a.dir * 2, a.radius, p2);
      dummy.position.copy(p);
      dummy.lookAt(p2);
      const w = 0.28 + intensity * 0.12;
      dummy.scale.set(w, w, 0.11 + intensity * 0.16);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (matRef.current) matRef.current.opacity = 0.5 + intensity * 0.4;
  });

  return (
    <instancedMesh ref={meshRef} args={[geo, undefined, count]} frustumCulled={false}>
      <meshBasicMaterial ref={matRef} color={CYAN} transparent opacity={0.65} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
    </instancedMesh>
  );
}

export { surgeVert, surgeFrag };
